import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ArrowLeft, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import FormTextField from 'components/IAP/formFields/FormTextField';
import type { ResourceRequestItem } from 'components/IAP/hooks/incidentResourceRequestHooks';
import type { LinkedIcs213rr } from 'components/IAP/hooks/resourceSetHooks';
import { Ics213RrFormBody } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrFormBody';
import {
  hasLinkedIcs213rr,
  linkedItemsToRequestItems,
  requestItemsToLinkedItems,
  withDefaultAssetRow,
  type LinkedIcs213rrAsset,
} from 'components/IAP/ResourceRequests/ics213rr/linkedIcs213rr';
import { AddRequestedResource } from './AddRequestedResource';

type LinkedFormValues = {
  name: string;
  supply_sources: string;
};

function AssetAttachChip({ name }: { name: string }) {
  return (
    <span
      className="tw:inline-flex tw:max-w-full tw:items-center tw:gap-1.5 tw:rounded-sm tw:bg-accent tw:px-1.5 tw:py-0.5 tw:text-xs tw:font-medium tw:text-accent-foreground"
      title="Attached to this asset; can't be changed"
    >
      <Lock className="tw:size-3 tw:shrink-0" aria-hidden="true" />
      <span className="tw:truncate">{name}</span>
    </span>
  );
}

function LinkedIcs213rrEditor({
  initial,
  asset,
  onSave,
  onCancel,
}: {
  initial: LinkedIcs213rr;
  asset: LinkedIcs213rrAsset;
  onSave: (next: LinkedIcs213rr) => void;
  onCancel: () => void;
}) {
  const assetName = asset.name?.trim() || 'This asset';
  const form = useForm<LinkedFormValues>({
    defaultValues: {
      name: initial.name ?? '',
      supply_sources: initial.supply_sources ?? '',
    },
  });
  const [items, setItems] = useState<ResourceRequestItem[]>(() =>
    withDefaultAssetRow(linkedItemsToRequestItems(initial.items), asset)
  );
  const incompleteAssetRow = items.findIndex(
    (item) => item.attached_to_asset && (!item.kind || !item.type)
  );
  const [showAdd, setShowAdd] = useState(incompleteAssetRow >= 0);
  const [editIndex, setEditIndex] = useState<number | null>(
    incompleteAssetRow >= 0 ? incompleteAssetRow : null
  );
  const attachChip = <AssetAttachChip name={assetName} />;

  const closeEditor = () => {
    setShowAdd(false);
    setEditIndex(null);
  };

  const save = form.handleSubmit((values) => {
    onSave({
      ...initial,
      name: values.name?.trim() || null,
      supply_sources: values.supply_sources?.trim() || null,
      items: requestItemsToLinkedItems(items),
    });
  });

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <div className="tw:flex tw:flex-col tw:gap-3">
          <FormTextField
            title="Request Name"
            fieldName="name"
            placeholder={asset.name ? `${asset.name} - 213RR` : undefined}
          />
          <Ics213RrFormBody
            mode="create"
            items={items}
            setItems={setItems}
            memberPositions={[]}
            editing
            costEditable
            canRemoveItem={(item) => !item.attached_to_asset}
            renderAttachCell={(item) =>
              item.attached_to_asset ? attachChip : null
            }
            addItemSlot={
              <div className="tw:mb-2 tw:flex tw:justify-end">
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setEditIndex(null);
                    setShowAdd(true);
                  }}
                >
                  + Add Row
                </Button>
              </div>
            }
            addingItem={showAdd && editIndex === null}
            editingItemIndex={showAdd ? editIndex : null}
            renderItemEditor={({ item, editIndex: index, logisticsCells }) => (
              <AddRequestedResource
                layout="row"
                setCustomResources={setItems}
                workAssignment={null}
                setShowAdd={setShowAdd}
                resourceToEdit={item}
                editIndex={index}
                onClose={closeEditor}
                trailingCells={logisticsCells}
                attachSlot={item?.attached_to_asset ? attachChip : undefined}
              />
            )}
            onEditItem={(index) => {
              setEditIndex(index);
              setShowAdd(true);
            }}
          />
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            <Button type="button" onClick={() => void save()}>
              Save Linked ICS 213RR
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

function summary(value: LinkedIcs213rr): string {
  const lineCount = value.items?.length ?? 0;
  const lines = `${lineCount} line item${lineCount === 1 ? '' : 's'}`;
  return value.name?.trim() ? `${value.name.trim()} (${lines})` : lines;
}

/**
 * Optional 213RR content saved on the asset. It is never signed here; "Create
 * ICS213RR" inside an incident workspace turns it into a full 213RR for that
 * incident. Editing happens in the same fullscreen modal a 213RR opens in from
 * a task.
 */
export function LinkedIcs213rrSection({
  value,
  asset,
  onChange,
}: {
  value: LinkedIcs213rr | null;
  asset: LinkedIcs213rrAsset;
  onChange: (next: LinkedIcs213rr | null) => void;
}) {
  const assetName = asset.name?.trim();
  const [open, setOpen] = useState(false);
  const saved = hasLinkedIcs213rr(value) ? value : null;

  return (
    <Field>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <FieldLabel>Linked ICS 213RR</FieldLabel>
        <div className="tw:flex tw:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
          >
            {saved ? 'Edit Linked ICS 213RR' : 'Add Linked ICS 213RR'}
          </Button>
          {saved ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(null)}
            >
              Remove
            </Button>
          ) : null}
        </div>
      </div>
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
        {saved
          ? summary(saved)
          : 'Optional. Prefilled request content for this asset. It is not signed here; use Create ICS213RR inside an incident to start a 213RR from it.'}
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="dark tw:!fixed tw:!inset-0 tw:!top-0 tw:!left-0 tw:!z-[80] tw:!flex tw:!h-dvh tw:!max-h-none tw:!w-screen tw:!max-w-none tw:!translate-x-0 tw:!translate-y-0 tw:!flex-col tw:!gap-0 tw:!rounded-none tw:!p-0 tw:sm:!max-w-none">
          <DialogHeader className="tw:shrink-0 tw:!flex-row tw:items-center tw:gap-3 tw:border-b tw:border-border tw:px-4 tw:py-3 tw:pr-14">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              <ArrowLeft />
              Back to asset
            </Button>
            <div className="tw:min-w-0">
              <DialogTitle>Linked ICS 213RR</DialogTitle>
              <DialogDescription>
                Prefilled, unsigned 213RR for {assetName || 'this asset'}.
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4 tw:py-4">
            {open ? (
              <LinkedIcs213rrEditor
                initial={value ?? { items: [] }}
                asset={asset}
                onCancel={() => setOpen(false)}
                onSave={(next) => {
                  onChange(hasLinkedIcs213rr(next) ? next : null);
                  setOpen(false);
                }}
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </Field>
  );
}
